import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { ArrowBack, PersonAdd } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar.tsx';
import { useAuth } from '../context/AuthContext.tsx';

const API_BASE = process.env.REACT_APP_API_URL + '/api';


const NieuweGebruiker: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    rol: '',
    voornaam: '',
    naam: '',
    afkorting: '',
    straatnaam: '',
    nummer: '',
    stad: '',
    postcode: '',
    email: '',
    telefoonnummer: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    document.title = 'Nieuwe Gebruiker - Thuisverpleging Portaal';
  }, []);

  const handleInputChange = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleBack = () => {
    navigate('/verpleegkundigen');
  };

  const handleSubmit = async (event: React.FormEvent) => {
  event.preventDefault();

  if (!formData.voornaam.trim() || !formData.naam.trim() || !formData.email.trim()) {
    setSnackbarMessage('Voornaam, naam en email zijn verplicht');
    setSnackbarOpen(true);
    return;
  }

  const token = localStorage.getItem("auth_token");

  try {
    const response = await fetch(`${API_BASE}/nurses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(formData)
    });

    const data = await response.json();

    if (!response.ok) {
      setSnackbarMessage(data.message || "Aanmaken mislukt");
      setSnackbarOpen(true);
      return;
    }

    setSnackbarMessage("Gebruiker succesvol aangemaakt!");
    setSnackbarOpen(true);

    setTimeout(() => navigate("/verpleegkundigen"), 1000);
  } catch (error) {
    console.error(error);
    setSnackbarMessage("Server fout bij aanmaken gebruiker");
    setSnackbarOpen(true);
  }
};


  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar open={true} onClose={() => {}} />
      
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ color: '#4aa8b4' }}>
              Nieuwe gebruiker
            </Typography>
          </Box>

          {/* Form */}
          <Paper sx={{ p: 4 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Rol */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Rol
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    placeholder="Select..."
                    value={formData.rol}
                    onChange={handleInputChange('rol')}
                    variant="outlined"
                  >
                    <MenuItem value="nurse">Verpleegkundige</MenuItem>
                    <MenuItem value="admin">Administrator</MenuItem>
                  </TextField>
                </Grid>

                {/* Voornaam, Naam, Afkorting */}
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" gutterBottom>
                    Voornaam
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="voornaam"
                    value={formData.voornaam}
                    onChange={handleInputChange('voornaam')}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" gutterBottom>
                    Naam
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="naam"
                    value={formData.naam}
                    onChange={handleInputChange('naam')}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" gutterBottom>
                    Afkorting
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="afkorting"
                    value={formData.afkorting}
                    onChange={handleInputChange('afkorting')}
                    variant="outlined"
                  />
                </Grid>

                {/* Straatnaam, Nummer, Stad, Postcode */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Straatnaam
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="straatnaam"
                    value={formData.straatnaam}
                    onChange={handleInputChange('straatnaam')}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Nummer
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="nummer"
                    value={formData.nummer}
                    onChange={handleInputChange('nummer')}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Stad
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="stad"
                    value={formData.stad}
                    onChange={handleInputChange('stad')}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Postcode
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange('postcode')}
                    variant="outlined"
                  />
                </Grid>

                {/* Email en Telefoonnummer */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Telefoonnummer
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="telefoonnummer"
                    value={formData.telefoonnummer}
                    onChange={handleInputChange('telefoonnummer')}
                    variant="outlined"
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<PersonAdd />}
                    sx={{
                      backgroundColor: '#4aa8b4',
                      '&:hover': {
                        backgroundColor: '#3a8b94'
                      },
                      mt: 2
                    }}
                  >
                    Aanmaken
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NieuweGebruiker;